import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonComments = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Documentation & Maintenance</div>
                <h1>Smart Commenting: The Art of Explanation</h1>
                <p className="hero-lead">
                    Code tells the machine <em>how</em> to do something. Comments tell the human <em>why</em> it is being done.
                    Master the delicate balance between helpful documentation and visual clutter.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Imagine walking into a massive library where the books have no covers, no titles, and are sorted randomly by color.
                    That is a codebase without comments. You might eventually find what you're looking for, but it will take hours of
                    frustrating skimming.
                </p>
                <p>
                    <strong>The Librarian Analogy:</strong> Think of your code logic as the "Books" (the actual content) and your comments
                    as the "Card Catalog" or "Librarian" (the metadata that helps you navigate). A good librarian doesn't read the
                    book out loud to you (redundant comments); they point you to the right section and warn you if a book is
                    outdated or fragile.
                </p>
                <p>
                    In professional software engineering, writing code is only 20% of the job. The other 80% is reading, debugging,
                    and maintaining code written by others (or your past self). High-quality documentation is what separates "write-only"
                    legacy scripts from enduring, maintainable software products.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Golden Rule:</strong> Explaining "Why" not "How".</li>
                        <li><strong>Docstrings (PEP 257):</strong> The industry standard for function documentation.</li>
                        <li><strong>Type Hinting:</strong> The modern replacement for variable comments.</li>
                        <li><strong>Technical Debt:</strong> Using TODO and FIXME tags effectively.</li>
                        <li><strong>Anti-Patterns:</strong> Avoiding "Zombie Comments" and clutter.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Golden Rule: "Why" vs "What"</h2>
                <p>
                    The single biggest mistake intermediates make is commenting on <em>syntax</em> rather than <em>intent</em>.
                    The code itself already explains the syntax. If your code is so complex that it needs a comment to explain
                    <em>what</em> it's doing, you should probably refactor the code (rename variables, break into functions) rather than comment it.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Bad Comment (The "Captain Obvious")</th>
                                <th>Good Comment (The "Navigator")</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Variables</strong></td>
                                <td>
                                    <code>timeout = 10  # Set timeout to 10</code>
                                </td>
                                <td>
                                    <code>timeout = 10  # Legacy API requirement (Ticket #404)</code>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>Logic</strong></td>
                                <td>
                                    <code>i += 1  # Increment i</code>
                                </td>
                                <td>
                                    <code>i += 1  # Compensate for 0-based indexing</code>
                                </td>
                            </tr>
                            <tr>
                                <td><strong>Conditionals</strong></td>
                                <td>
                                    <code>if x &lt; 0:  # If x is less than 0</code>
                                </td>
                                <td>
                                    <code>if x &lt; 0:  # Negative balance triggers overdraft protection</code>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Refactoring Over Commenting</span>
                    </div>
                    <CodeBlock language="python" code={`#  BAD: Complex logic with explanatory comments
# Check if user is over 18 and has active subscription
if u.age &gt; 18 and u.status == 'active' and u.balance &gt; 0:
    process_order()

# GOOD: Self-documenting code (No comments needed!)
def is_eligible_for_purchase(user):
    return (
        user.is_adult 
        and user.has_active_subscription 
        and user.has_positive_balance
    )

if is_eligible_for_purchase(user):
    process_order()`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Docstrings: The Professional Standard</h2>
                <p>
                    While regular comments (<code>#</code>) are for the developer maintaining the implementation, <strong>Docstrings</strong> (Documentation Strings)
                    are for the developer <em>using</em> your interface. They are a formal specification of input, output, and behavior.
                </p>
                <p>
                    Python treats Docstrings differently than comments. They are retained at runtime in the <code>__doc__</code> attribute, allowing
                    tools like IDEs (VS Code, PyCharm) to pop up helpful tooltips when you type a function name.
                </p>

                <h3>The Google Style Guide</h3>
                <p>
                    There are several formats for docstrings (reStructuredText, NumPy, Epytext), but the <strong>Google Style</strong> is widely
                    considered the most readable for humans.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Anatomy of a Perfect Docstring</span>
                    </div>
                    <CodeBlock language="python" code={`def connect_to_database(url: str, retries: int = 3) -&gt; bool:
    """
    Establishes a secure connection to the primary database replica.

    Backs off exponentially if the connection fails, up to the specified
    number of retries. Uses SSL by default.

    Args:
        url (str): The JDBC or ODBC connection string.
            Example: 'postgresql://user:pass@localhost:5432/db'
        retries (int, optional): Max connection attempts before giving up. 
            Defaults to 3.

    Returns:
        bool: True if connection successful, False if all retries failed.

    Raises:
        ConnectionTimeout error: If the server is reachable but unresponsive.
        ValueError: If the URL format is invalid.
    """
    # Implementation hidden...
    return True`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Try it yourself:</strong> Copy that function into VS Code. Then, in a new line, type <code>connect_to_database(</code>.
                        Notice how VS Code displays the "Args" and "Returns" info? That is the power of Docstrings interacting with the Pylance language server.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Managing Technical Debt: TODO and FIXME</h2>
                <p>
                    Software is never finished. Sometimes you need to leave a placeholder, mark a hacky fix, or note an area for optimization.
                    Instead of random notes, professional teams use standardized "Tags".
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Project Tags</span>
                    </div>
                    <CodeBlock language="python" code={`def calculate_tax(amount):
    # TODO: Switch to external TaxJar API for international support
    rate = 0.20
    
    # FIXME: This crashes if amount is negative!
    # HACK: Use abs() for now until validation is fixed upstream
    return abs(amount) * rate

    # OPTIMIZE: This nested loop is O(n^2), refactor to use a hash map
    for item in items:
        # ...`} />
                </div>

                <p>
                    <strong>Why use these keywords?</strong>
                </p>
                <ul>
                    <li><strong>Searchability:</strong> You can grep your entire project for <code>TODO</code> to generate a task list.</li>
                    <li><strong>IDE Integration:</strong> VS Code extensions like "Todo Tree" explicitly highlight these in the sidebar.</li>
                    <li><strong>CI/CD Safety:</strong> Some build pipelines (like in Rust or Go) can be configured to fail deployment if <code>FIXME</code> tags remain in main.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Comments vs Type Hinting</h2>
                <p>
                    Before Python 3.5, developers often used comments to specify types. This is now considered "Legacy" practice.
                    Modern Python uses <strong>Type Hints</strong> (PEP 484), which are standard, readable, and verifiable by tools.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Evolution of Type Syntax</span>
                    </div>
                    <CodeBlock language="python" code={`#  LEGACY (Pre-2015): Using comments (Fragile)
def greet(name):
    # type: (str) -&gt; str
    return "Hi " + name

# MODERN (Recommended): Using Type Hints (Robust)
def greet(name: str) -&gt; str:
    return f"Hi {name}"`} />
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Common Anti-Patterns</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3> The Zombie Comment</h3>
                        <p>
                            Code that is commented out instead of deleted. "I might need this later."
                        </p>
                        <p><strong>Fix:</strong> Delete it. That's what Git history is for. If you need it back, look at the commit log.</p>
                    </div>
                    <div className="practice-card">
                        <h3> The Liar</h3>
                        <p>
                            A comment that contradicts the code. (e.g., Code says <code>x = 10</code>, Comment says <code># Set x to 5</code>).
                        </p>
                        <p><strong>Fix:</strong> This happens when you update code but forget to update the comment. Trust the code, delete the comment.</p>
                    </div>
                </div>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>Why vs How</h3>
                        <p>Code explains how. Comments explain why. If the "how" is unclear, refactor the code instead of adding comments.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Docstrings</h3>
                        <p>Use Triple-Quotes (<code>"""</code>) for functions and classes. Follow the Google Style Guide for args and returns.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>No Zombies</h3>
                        <p>Never commit commented-out code. Use Version Control (Git) to save history.</p>
                    </div>
                    <div className="takeaway-item">
                        <h3>Standard Tags</h3>
                        <p>Use <code>TODO</code>, <code>FIXME</code>, and <code>HACK</code> ensures your team can find technical debt easily.</p>
                    </div>
                </div>
            </div>

            <div className="external-resources">
                <h3> Further Reading</h3>
                <div className="resource-links">
                    <a href="https://peps.python.org/pep-0008/#comments" target="_blank" rel="noopener noreferrer">
                        PEP 8: Official Commenting Style Guidelines
                    </a>
                    <a href="https://peps.python.org/pep-0257/" target="_blank" rel="noopener noreferrer">
                        PEP 257: Docstring Conventions
                    </a>
                    <a href="https://google.github.io/styleguide/pyguide.html#38-comments-and-docstrings" target="_blank" rel="noopener noreferrer">
                        Google Python Style Guide
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PythonComments;


